import {
  PROVISIONAL_PHYSICS_CONFIG,
  type PhysicsConfig,
} from "./config.ts";
import type {
  AuthoredStaticCollisionSpec,
  CameraCastRequest,
  CameraCastResult,
  CharacterCapsuleSpec,
  CharacterContact,
  CharacterMotionRequest,
  CharacterMotionResolution,
  GroundProbeResult,
  MantleQueryResult,
  PhysicsPlanarVector,
  PhysicsSurfaceMetadata,
  PhysicsVector3,
  PhysicsWorldStats,
  TileColliderSummary,
  VehicleColliderSpec,
  VehicleMotionResolution,
  VilniusPhysicsWorld,
} from "./contracts.ts";
import {
  createTileColliders,
  type AttachedTileColliders,
} from "./tileColliders.ts";
import {
  initializeRapier,
  type RapierModule,
} from "./rapierRuntime.ts";
import type { RuntimeWorldTilePayloadV2 } from "../world/schema/runtimeTile.ts";
import type {
  AuthoredBridgeCollisionSpec,
} from "../world/bridgeCollisions.ts";

type RapierCollider = import("@dimforge/rapier3d").Collider;
type RapierCharacterController =
  import("@dimforge/rapier3d").KinematicCharacterController;
type RapierWorld = import("@dimforge/rapier3d").World;
type RapierRigidBody = import("@dimforge/rapier3d").RigidBody;

const UP = Object.freeze({ x: 0, y: 1, z: 0 });
const IDENTITY_ROTATION = Object.freeze({ x: 0, y: 0, z: 0, w: 1 });
const MIN_VECTOR_LENGTH = 1e-6;
const CAPSULE_MATCH_EPSILON = 1e-6;
const STEP_DETECTION_EPSILON = 1e-4;

interface AttachedCharacter {
  collider: RapierCollider;
  controller: RapierCharacterController;
  capsule: CharacterCapsuleSpec;
  maxSlopeAngleRadians: number;
  maxStepHeightM: number;
}

interface AttachedVehicle {
  readonly body: RapierRigidBody;
  readonly collider: RapierCollider;
  readonly size: PhysicsVector3;
  readonly groundContactHalfExtents: PhysicsPlanarVector;
}

export interface CreateVilniusPhysicsWorldOptions {
  config?: Partial<PhysicsConfig>;
  bridgeCollisions?: readonly AuthoredBridgeCollisionSpec[];
  authoredColliders?: readonly AuthoredStaticCollisionSpec[];
}

/**
 * Initializes Rapier's WASM module and creates the M3 project-owned physics
 * boundary. All Rapier handles remain private to this adapter.
 */
export async function createVilniusPhysicsWorld(
  options: Readonly<CreateVilniusPhysicsWorldOptions> = {},
): Promise<VilniusPhysicsWorld> {
  const rapier = await initializeRapier();
  const config = Object.freeze({
    ...PROVISIONAL_PHYSICS_CONFIG,
    ...options.config,
  });
  return new RapierPhysicsWorld(
    rapier,
    config,
    options.bridgeCollisions ?? [],
    options.authoredColliders ?? [],
  );
}

class RapierPhysicsWorld implements VilniusPhysicsWorld {
  readonly #rapier: RapierModule;
  readonly #world: RapierWorld;
  readonly #config: Readonly<PhysicsConfig>;
  readonly #tiles = new Map<string, AttachedTileColliders>();
  readonly #characters = new Map<string, AttachedCharacter>();
  readonly #vehicles = new Map<string, AttachedVehicle>();
  readonly #surfaces = new Map<number, PhysicsSurfaceMetadata>();
  readonly #bridgeCollisionsByTile = new Map<
    string,
    readonly AuthoredBridgeCollisionSpec[]
  >();
  readonly #authoredColliders = new Map<
    string,
    Readonly<{ collider: RapierCollider; spec: AuthoredStaticCollisionSpec }>
  >();
  #disposed = false;
  #sceneQueriesDirty = false;

  constructor(
    rapier: RapierModule,
    config: Readonly<PhysicsConfig>,
    bridgeCollisions: readonly AuthoredBridgeCollisionSpec[],
    authoredColliders: readonly AuthoredStaticCollisionSpec[],
  ) {
    this.#rapier = rapier;
    this.#config = config;
    // The simulation layer owns gravity integration. This query/resolution
    // world contains static tile colliders and parentless character colliders.
    this.#world = new rapier.World({ x: 0, y: 0, z: 0 });
    const grouped = new Map<string, AuthoredBridgeCollisionSpec[]>();
    for (const bridge of bridgeCollisions) {
      const tileBridges = grouped.get(bridge.tileId) ?? [];
      tileBridges.push(bridge);
      grouped.set(bridge.tileId, tileBridges);
    }
    for (const [tileId, tileBridges] of grouped) {
      this.#bridgeCollisionsByTile.set(tileId, Object.freeze(tileBridges));
    }
    for (const spec of authoredColliders) {
      if (this.#authoredColliders.has(spec.id)) {
        throw new Error(`Duplicate authored collider ${spec.id}`);
      }
      assertStableId(spec.id, "Authored collider ID");
      assertVector(spec.position, `Authored collider ${spec.id} position`);
      assertVector(spec.halfExtentsM, `Authored collider ${spec.id} half-extents`);
      assertPositive(spec.halfExtentsM.x, `Authored collider ${spec.id} width`);
      assertPositive(spec.halfExtentsM.y, `Authored collider ${spec.id} height`);
      assertPositive(spec.halfExtentsM.z, `Authored collider ${spec.id} depth`);
      const halfYaw = spec.rotationYRadians / 2;
      const collider = this.#world.createCollider(
        rapier.ColliderDesc.cuboid(
          spec.halfExtentsM.x,
          spec.halfExtentsM.y,
          spec.halfExtentsM.z,
        )
          .setTranslation(spec.position.x, spec.position.y, spec.position.z)
          .setRotation({
            x: 0,
            y: Math.sin(halfYaw),
            z: 0,
            w: Math.cos(halfYaw),
          })
          .setFriction(config.buildingFriction)
          .setEnabled(spec.enabled),
      );
      this.#registerSurface(collider, {
        colliderId: `authored:${spec.id}`,
        surfaceId: `authored:${spec.id}`,
        classification: spec.classification,
        sourceFeatureId: spec.sourceFeatureId ?? spec.id,
      });
      this.#authoredColliders.set(spec.id, { collider, spec: { ...spec } });
    }
    this.#sceneQueriesDirty = this.#authoredColliders.size > 0;
  }

  attachTile(
    tile: Readonly<RuntimeWorldTilePayloadV2>,
  ): TileColliderSummary {
    this.#assertUsable();
    if (this.#tiles.has(tile.id)) {
      throw new Error(`Physics tile ${tile.id} is already attached`);
    }
    const attached = this.#createTile(tile);
    this.#tiles.set(tile.id, attached);
    this.#sceneQueriesDirty = true;
    return { ...attached.summary };
  }

  updateTile(
    tile: Readonly<RuntimeWorldTilePayloadV2>,
  ): TileColliderSummary {
    this.#assertUsable();
    const previous = this.#tiles.get(tile.id);
    if (previous === undefined) {
      throw new Error(`Physics tile ${tile.id} is not attached`);
    }

    // Build first so a malformed update cannot remove the last valid tile.
    const replacement = this.#createTile(tile);
    this.#removeTileColliders(previous);
    this.#tiles.set(tile.id, replacement);
    this.#sceneQueriesDirty = true;
    return { ...replacement.summary };
  }

  detachTile(tileId: string): boolean {
    this.#assertUsable();
    const attached = this.#tiles.get(tileId);
    if (attached === undefined) {
      return false;
    }
    this.#removeTileColliders(attached);
    this.#tiles.delete(tileId);
    this.#sceneQueriesDirty = true;
    return true;
  }

  hasTile(tileId: string): boolean {
    this.#assertUsable();
    return this.#tiles.has(tileId);
  }

  setAuthoredColliderEnabled(id: string, enabled: boolean): boolean {
    this.#assertUsable();
    const authored = this.#authoredColliders.get(id);
    if (authored === undefined) {
      return false;
    }
    authored.collider.setEnabled(enabled);
    this.#sceneQueriesDirty = true;
    return true;
  }

  attachCharacter(
    playerId: string,
    position: Readonly<PhysicsVector3>,
    capsule: Readonly<CharacterCapsuleSpec> = {
      radiusM: this.#config.characterRadiusM,
      halfHeightM: this.#config.characterHalfHeightM,
    },
  ): void {
    this.#assertUsable();
    assertStableId(playerId, "Player ID");
    assertVector(position, "Character position");
    assertPositive(capsule.radiusM, "Character capsule radius");
    assertPositive(capsule.halfHeightM, "Character capsule half-height");
    if (this.#characters.has(playerId)) {
      throw new Error(`Physics character ${playerId} is already attached`);
    }

    const collider = this.#world.createCollider(
      this.#rapier.ColliderDesc.capsule(
        capsule.halfHeightM,
        capsule.radiusM,
      ).setTranslation(
        position.x,
        position.y + characterCenterOffsetM(capsule, this.#config),
        position.z,
      ),
    );
    const controller = this.#world.createCharacterController(
      this.#config.characterContactOffsetM,
    );
    controller.setUp(UP);
    controller.setApplyImpulsesToDynamicBodies(false);
    controller.setNormalNudgeFactor(this.#config.controllerNormalNudgeM);
    controller.setSlideEnabled(true);
    controller.disableSnapToGround();

    this.#registerSurface(collider, {
      colliderId: `character:${playerId}`,
      surfaceId: `character:${playerId}`,
      classification: "character",
      sourceFeatureId: playerId,
    });
    this.#characters.set(playerId, {
      collider,
      controller,
      capsule: { ...capsule },
      maxSlopeAngleRadians: Math.PI / 4,
      maxStepHeightM: 0,
    });
    this.#sceneQueriesDirty = true;
  }

  placeCharacter(
    playerId: string,
    position: Readonly<PhysicsVector3>,
    capsule: Readonly<CharacterCapsuleSpec> = {
      radiusM: this.#config.characterRadiusM,
      halfHeightM: this.#config.characterHalfHeightM,
    },
  ): void {
    this.#assertUsable();
    assertStableId(playerId, "Player ID");
    assertVector(position, "Character position");
    assertPositive(capsule.radiusM, "Character capsule radius");
    assertPositive(capsule.halfHeightM, "Character capsule half-height");
    const character = this.#characters.get(playerId);
    if (character === undefined) {
      this.attachCharacter(playerId, position, capsule);
      return;
    }

    this.#assertMatchingCapsule(character, capsule);
    character.collider.setTranslation(
      characterCenterFromFootPosition(
        position,
        character.capsule,
        this.#config,
      ),
    );
    this.#sceneQueriesDirty = true;
  }

  detachCharacter(playerId: string): boolean {
    this.#assertUsable();
    const character = this.#characters.get(playerId);
    if (character === undefined) {
      return false;
    }
    this.#world.removeCharacterController(character.controller);
    this.#surfaces.delete(character.collider.handle);
    this.#world.removeCollider(character.collider, false);
    this.#characters.delete(playerId);
    this.#sceneQueriesDirty = true;
    return true;
  }

  attachVehicle(spec: Readonly<VehicleColliderSpec>): void {
    this.#assertUsable();
    assertStableId(spec.id, "Vehicle ID");
    assertVector(spec.position, `Vehicle ${spec.id} position`);
    assertVector(spec.bodySizeM, `Vehicle ${spec.id} body size`);
    assertPositive(
      spec.groundContactHalfExtentsM.x,
      `Vehicle ${spec.id} ground-contact half width`,
    );
    assertPositive(
      spec.groundContactHalfExtentsM.z,
      `Vehicle ${spec.id} ground-contact half wheelbase`,
    );
    if (this.#vehicles.has(spec.id)) throw new Error(`Physics vehicle ${spec.id} is already attached`);
    const half = scaleVector(spec.bodySizeM, 0.5);
    if (
      spec.groundContactHalfExtentsM.x > half.x ||
      spec.groundContactHalfExtentsM.z > half.z
    ) {
      throw new Error(
        `Vehicle ${spec.id} ground contacts must stay inside its body footprint`,
      );
    }
    const rotation = vehicleRotation(
      spec.yawRadians,
      spec.pitchRadians,
      spec.rollRadians,
    );
    const body = this.#world.createRigidBody(
      this.#rapier.RigidBodyDesc.kinematicPositionBased()
        .setTranslation(spec.position.x, spec.position.y + half.y, spec.position.z)
        .setRotation(rotation),
    );
    const collider = this.#world.createCollider(
      this.#rapier.ColliderDesc.cuboid(half.x, half.y, half.z)
        .setFriction(this.#config.buildingFriction),
      body,
    );
    this.#registerSurface(collider, {
      colliderId: `vehicle:${spec.id}`,
      surfaceId: `vehicle:${spec.id}`,
      classification: "vehicle",
      sourceFeatureId: spec.id,
    });
    this.#vehicles.set(spec.id, {
      body,
      collider,
      size: { ...spec.bodySizeM },
      groundContactHalfExtents: { ...spec.groundContactHalfExtentsM },
    });
    this.#sceneQueriesDirty = true;
  }

  detachVehicle(id: string): boolean {
    this.#assertUsable();
    const vehicle = this.#vehicles.get(id);
    if (vehicle === undefined) {
      return false;
    }
    this.#surfaces.delete(vehicle.collider.handle);
    this.#world.removeRigidBody(vehicle.body);
    this.#vehicles.delete(id);
    this.#sceneQueriesDirty = true;
    return true;
  }

  placeVehicle(
    id: string,
    position: Readonly<PhysicsVector3>,
    yawRadians: number,
    pitchRadians: number,
    rollRadians: number,
  ): void {
    this.#assertUsable();
    assertVector(position, `Vehicle ${id} position`);
    const vehicle = this.#getVehicle(id);
    vehicle.body.setTranslation({ x: position.x, y: position.y + vehicle.size.y / 2, z: position.z }, true);
    vehicle.body.setRotation(
      vehicleRotation(yawRadians, pitchRadians, rollRadians),
      true,
    );
    this.#sceneQueriesDirty = true;
  }

  resolveVehicleMotion(request: Readonly<{
    vehicleId: string;
    currentPosition: PhysicsVector3;
    proposedPosition: PhysicsVector3;
    currentYawRadians: number;
    proposedYawRadians: number;
    currentPitchRadians: number;
    currentRollRadians: number;
    maximumStepHeightM: number;
    maximumGroundTiltRadians: number;
  }>): VehicleMotionResolution {
    this.#assertUsable();
    const vehicle = this.#getVehicle(request.vehicleId);
    this.#synchronizeSceneQueries();
    const displacement = subtractVectors(request.proposedPosition, request.currentPosition);
    const distance = vectorLength(displacement);
    const direction = distance <= MIN_VECTOR_LENGTH ? { x: 0, y: 0, z: 0 } : scaleVector(displacement, 1 / distance);
    const half = scaleVector(vehicle.size, 0.5);
    const hit = distance <= MIN_VECTOR_LENGTH ? null : this.#world.castShape(
      {
        x: request.currentPosition.x,
        y: request.currentPosition.y + half.y + request.maximumStepHeightM,
        z: request.currentPosition.z,
      },
      vehicleRotation(
        request.proposedYawRadians,
        request.currentPitchRadians,
        request.currentRollRadians,
      ),
      direction,
      new this.#rapier.Cuboid(half.x, half.y, half.z),
      0,
      distance + 0.04,
      true,
      undefined,
      undefined,
      vehicle.collider,
    );
    const planar = hit === null ? request.proposedPosition : request.currentPosition;
    const ground = this.#vehicleGroundPose(
      planar,
      hit === null ? request.proposedYawRadians : request.currentYawRadians,
      vehicle,
    );
    const groundUnavailable = ground === null;
    const climbsTooHigh = ground !== null &&
      ground.elevationM - request.currentPosition.y >
        request.maximumStepHeightM + MIN_VECTOR_LENGTH;
    const tiltsTooFar = ground !== null &&
      Math.max(Math.abs(ground.pitchRadians), Math.abs(ground.rollRadians)) >
        request.maximumGroundTiltRadians;
    const blocked = hit !== null || groundUnavailable || climbsTooHigh || tiltsTooFar;
    const position = blocked
      ? { ...request.currentPosition }
      : { ...planar, y: ground.elevationM };
    const yawRadians = blocked
      ? request.currentYawRadians
      : request.proposedYawRadians;
    const pitchRadians = blocked
      ? request.currentPitchRadians
      : ground.pitchRadians;
    const rollRadians = blocked
      ? request.currentRollRadians
      : ground.rollRadians;
    this.placeVehicle(
      request.vehicleId,
      position,
      yawRadians,
      pitchRadians,
      rollRadians,
    );
    return {
      position,
      yawRadians,
      pitchRadians,
      rollRadians,
      blocked,
      obstructionId: hit === null ? null : this.#surfaceFor(hit.collider).colliderId,
      groundSurface: ground?.surface ?? null,
    };
  }

  resolveVehicleExit(request: Readonly<{
    vehicleId: string;
    candidates: readonly PhysicsVector3[];
    capsule: CharacterCapsuleSpec;
  }>): PhysicsVector3 | null {
    this.#assertUsable();
    const vehicle = this.#getVehicle(request.vehicleId);
    this.#synchronizeSceneQueries();
    const centerOffset = characterCenterOffsetM(request.capsule, this.#config);
    for (const candidate of request.candidates) {
      const ray = this.#world.castRay(
        new this.#rapier.Ray({ x: candidate.x, y: candidate.y + 2.2, z: candidate.z }, { x: 0, y: -1, z: 0 }),
        4.4,
        true,
        undefined,
        undefined,
        vehicle.collider,
      );
      if (ray === null) continue;
      const grounded = { x: candidate.x, y: candidate.y + 2.2 - ray.timeOfImpact, z: candidate.z };
      const overlap = this.#world.intersectionWithShape(
        { x: grounded.x, y: grounded.y + centerOffset, z: grounded.z },
        IDENTITY_ROTATION,
        new this.#rapier.Capsule(request.capsule.halfHeightM, request.capsule.radiusM),
      );
      if (overlap === null) return grounded;
    }
    return null;
  }

  resolveCharacterMotion(
    request: Readonly<CharacterMotionRequest>,
  ): CharacterMotionResolution {
    this.#assertUsable();
    assertTick(request.tick);
    assertPositive(request.deltaSeconds, "Character motion deltaSeconds");
    assertVector(request.currentPosition, "Character current position");
    assertVector(request.currentVelocity, "Character current velocity");
    assertVector(request.desiredMovement, "Character desired movement");
    assertFiniteNonNegative(
      request.maxStepHeightM,
      "Character maximum step height",
    );
    assertSlopeAngle(request.maxSlopeAngleRadians);
    assertFiniteNonNegative(
      request.maxMantleHeightM,
      "Character maximum mantle height",
    );

    const character = this.#getCharacter(request.playerId);
    this.#assertMatchingCapsule(character, request.capsule);
    character.maxSlopeAngleRadians = request.maxSlopeAngleRadians;
    character.maxStepHeightM = request.maxStepHeightM;
    character.collider.setTranslation(
      characterCenterFromFootPosition(
        request.currentPosition,
        character.capsule,
        this.#config,
      ),
    );
    this.#sceneQueriesDirty = true;

    const controller = character.controller;
    if (request.maxStepHeightM > 0) {
      controller.enableAutostep(
        request.maxStepHeightM,
        this.#config.autostepMinimumWidthM,
        false,
      );
    } else {
      controller.disableAutostep();
    }
    controller.setMaxSlopeClimbAngle(request.maxSlopeAngleRadians);
    controller.setMinSlopeSlideAngle(
      Math.max(
        request.maxSlopeAngleRadians,
        this.#config.minimumSlopeSlideAngleRadians,
      ),
    );
    if (
      request.groundedLastTick &&
      !request.jumpRequested &&
      request.desiredMovement.y <= 0
    ) {
      controller.enableSnapToGround(this.#config.groundProbeDistanceM);
    } else {
      controller.disableSnapToGround();
    }

    this.#synchronizeSceneQueries();
    const mantle = request.mantleRequested
      ? this.#queryCharacterMantle(
          character,
          request.forward,
          request.maxMantleHeightM,
        )
      : unavailableMantle();
    if (mantle.possible && mantle.targetPosition !== null) {
      character.collider.setTranslation(
        characterCenterFromFootPosition(
          mantle.targetPosition,
          character.capsule,
          this.#config,
        ),
      );
      this.#sceneQueriesDirty = true;
      const landingProbe = this.#probeCharacterGround(
        character,
        this.#config.groundProbeDistanceM,
      );
      return {
        playerId: request.playerId,
        tick: request.tick,
        resolvedPosition: { ...mantle.targetPosition },
        resolvedVelocity: { x: 0, y: 0, z: 0 },
        grounded: landingProbe.grounded,
        groundNormal: landingProbe.normal,
        groundSlopeAngleRadians: landingProbe.slopeAngleRadians,
        surfaceId:
          landingProbe.surface?.surfaceId ??
          mantle.landingSurface?.surfaceId ??
          null,
        stepped: false,
        mantle,
        contacts: [],
      };
    }

    const usesGroundSnapProbe =
      request.groundedLastTick &&
      !request.jumpRequested &&
      request.desiredMovement.y <= 0;
    const controllerMovement = usesGroundSnapProbe
      ? {
          ...request.desiredMovement,
          y: Math.min(
            request.desiredMovement.y,
            -this.#config.controllerNormalNudgeM,
          ),
        }
      : request.desiredMovement;
    controller.computeColliderMovement(
      character.collider,
      controllerMovement,
      undefined,
      undefined,
      (candidate) => candidate.handle !== character.collider.handle,
    );
    const movement = copyVector(controller.computedMovement());
    const resolvedPosition = addVectors(request.currentPosition, movement);
    character.collider.setTranslation(
      characterCenterFromFootPosition(
        resolvedPosition,
        character.capsule,
        this.#config,
      ),
    );
    this.#sceneQueriesDirty = true;

    const contacts = this.#readCharacterContacts(controller);
    const controllerGrounded = controller.computedGrounded();
    // During an autostep Rapier can report grounded while the capsule is
    // temporarily higher than the ordinary snap probe above the lower tread.
    // Probe through the full allowed riser in that transition so simulation
    // still receives the required ground normal and surface metadata.
    const probe = this.#probeCharacterGround(
      character,
      controllerGrounded
        ? Math.max(
            this.#config.groundProbeDistanceM,
            character.maxStepHeightM +
              this.#config.characterContactOffsetM,
          )
        : this.#config.groundProbeDistanceM,
    );
    const supportingContact = contacts
      .filter((contact) => {
        const slopeAngleRadians = Math.acos(
          clamp(contact.normal.y, -1, 1),
        );
        return contact.normal.y > 0 &&
          slopeAngleRadians <= character.maxSlopeAngleRadians;
      })
      .sort((left, right) => right.normal.y - left.normal.y)[0] ?? null;
    // A vertical ray can fall exactly between adjacent stair primitives while
    // Rapier still correctly reports the capsule as supported by their edge.
    // Prefer the ray when it finds walkable ground, then fall back to the
    // controller's upward support contact. Never publish a grounded result
    // without the normal and surface metadata required by simulation.
    const groundNormal = probe.grounded
      ? probe.normal
      : supportingContact?.normal ?? null;
    const groundSlopeAngleRadians = probe.grounded
      ? probe.slopeAngleRadians
      : groundNormal === null
        ? null
        : Math.acos(clamp(groundNormal.y, -1, 1));
    const groundSurfaceId = probe.grounded
      ? probe.surface?.surfaceId ?? null
      : supportingContact?.surface.surfaceId ?? null;
    const grounded = !request.jumpRequested &&
      request.desiredMovement.y <= 0 &&
      (
        probe.grounded ||
        (controllerGrounded && supportingContact !== null)
      );
    const probeSnapDistanceM =
      usesGroundSnapProbe &&
        probe.grounded &&
        probe.distanceToGroundM !== null &&
        probe.surface?.surfaceId.startsWith("bridge:") === true
        ? probe.distanceToGroundM
        : 0;
    const groundedPosition = probeSnapDistanceM > MIN_VECTOR_LENGTH
      ? {
          ...resolvedPosition,
          y: resolvedPosition.y - probeSnapDistanceM,
        }
      : resolvedPosition;
    if (groundedPosition !== resolvedPosition) {
      character.collider.setTranslation(
        characterCenterFromFootPosition(
          groundedPosition,
          character.capsule,
          this.#config,
        ),
      );
      this.#sceneQueriesDirty = true;
    }
    const movedPlanar = Math.hypot(
      request.desiredMovement.x,
      request.desiredMovement.z,
    ) > MIN_VECTOR_LENGTH;
    const stepped = movedPlanar &&
      movement.y >
        request.desiredMovement.y + STEP_DETECTION_EPSILON;
    return {
      playerId: request.playerId,
      tick: request.tick,
      resolvedPosition: groundedPosition,
      resolvedVelocity: {
        x: movement.x / request.deltaSeconds,
        // The extra downward movement exists only to keep the controller
        // attached to descending ground. It must not become public fall
        // velocity when the character walks down a slope or off an edge.
        y: usesGroundSnapProbe
          ? request.desiredMovement.y / request.deltaSeconds
          : movement.y / request.deltaSeconds,
        z: movement.z / request.deltaSeconds,
      },
      grounded,
      groundNormal,
      groundSlopeAngleRadians,
      surfaceId: groundSurfaceId,
      stepped,
      mantle,
      contacts,
    };
  }

  probeGround(
    playerId: string,
    maxDistanceM = this.#config.groundProbeDistanceM,
  ): GroundProbeResult {
    this.#assertUsable();
    assertFiniteNonNegative(maxDistanceM, "Ground probe distance");
    this.#synchronizeSceneQueries();
    return this.#probeCharacterGround(
      this.#getCharacter(playerId),
      maxDistanceM,
    );
  }

  queryMantle(
    playerId: string,
    forward: Readonly<PhysicsPlanarVector>,
    maxHeightM: number,
  ): MantleQueryResult {
    this.#assertUsable();
    assertFiniteNonNegative(maxHeightM, "Mantle maximum height");
    this.#synchronizeSceneQueries();
    return this.#queryCharacterMantle(
      this.#getCharacter(playerId),
      forward,
      maxHeightM,
    );
  }

  castCamera(
    request: Readonly<CameraCastRequest>,
  ): CameraCastResult {
    this.#assertUsable();
    assertVector(request.target, "Camera cast target");
    assertVector(
      request.desiredCameraPosition,
      "Camera cast desired position",
    );
    this.#synchronizeSceneQueries();
    const radiusM = request.radiusM ?? this.#config.cameraCollisionRadiusM;
    assertPositive(radiusM, "Camera cast radius");
    const delta = subtractVectors(
      request.desiredCameraPosition,
      request.target,
    );
    const desiredDistanceM = vectorLength(delta);
    if (desiredDistanceM <= MIN_VECTOR_LENGTH) {
      return {
        obstructed: false,
        resolvedCameraPosition: { ...request.target },
        desiredDistanceM: 0,
        resolvedDistanceM: 0,
        obstructionIds: [],
        surfaces: [],
      };
    }

    const direction = scaleVector(delta, 1 / desiredDistanceM);
    const excluded = request.excludePlayerId === undefined
      ? undefined
      : this.#characters.get(request.excludePlayerId)?.collider;
    const excludedIds = new Set(request.excludeColliderIds ?? []);
    const hit = this.#world.castShape(
      request.target,
      IDENTITY_ROTATION,
      direction,
      new this.#rapier.Ball(radiusM),
      0,
      desiredDistanceM,
      true,
      undefined,
      undefined,
      excluded,
      undefined,
      (candidate) => !excludedIds.has(this.#surfaceFor(candidate).colliderId),
    );
    if (hit === null) {
      return {
        obstructed: false,
        resolvedCameraPosition: { ...request.desiredCameraPosition },
        desiredDistanceM,
        resolvedDistanceM: desiredDistanceM,
        obstructionIds: [],
        surfaces: [],
      };
    }

    const resolvedDistanceM = Math.max(
      0,
      hit.time_of_impact - this.#config.cameraCollisionSkinM,
    );
    const surface = this.#surfaceFor(hit.collider);
    return {
      obstructed: true,
      resolvedCameraPosition: addVectors(
        request.target,
        scaleVector(direction, resolvedDistanceM),
      ),
      desiredDistanceM,
      resolvedDistanceM,
      obstructionIds: [surface.colliderId],
      surfaces: [surface],
    };
  }

  step(deltaSeconds: number): void {
    this.#assertUsable();
    assertPositive(deltaSeconds, "Physics timestep");
    this.#world.timestep = deltaSeconds;
    this.#world.step();
    this.#sceneQueriesDirty = false;
  }

  stats(): PhysicsWorldStats {
    this.#assertUsable();
    return {
      attachedTileIds: [...this.#tiles.keys()].sort(),
      attachedCharacterIds: [...this.#characters.keys()].sort(),
      staticColliderCount: [...this.#tiles.values()].reduce(
        (total, tile) => total + tile.colliders.length,
        0,
      ) + [...this.#authoredColliders.values()].filter(
        ({ collider }) => collider.isEnabled(),
      ).length,
      characterColliderCount: this.#characters.size,
      vehicleColliderCount: this.#vehicles.size,
    };
  }

  dispose(): void {
    if (this.#disposed) {
      return;
    }
    for (const character of this.#characters.values()) {
      this.#world.removeCharacterController(character.controller);
      this.#world.removeCollider(character.collider, false);
    }
    this.#characters.clear();
    for (const vehicle of this.#vehicles.values()) {
      this.#surfaces.delete(vehicle.collider.handle);
      this.#world.removeRigidBody(vehicle.body);
    }
    this.#vehicles.clear();
    for (const tile of this.#tiles.values()) {
      for (const collider of tile.colliders) {
        this.#world.removeCollider(collider, false);
      }
    }
    this.#tiles.clear();
    for (const { collider } of this.#authoredColliders.values()) {
      this.#world.removeCollider(collider, false);
    }
    this.#authoredColliders.clear();
    this.#surfaces.clear();
    this.#world.free();
    this.#disposed = true;
  }

  #createTile(
    tile: Readonly<RuntimeWorldTilePayloadV2>,
  ): AttachedTileColliders {
    assertStableId(tile.id, "Tile ID");
    return createTileColliders(
      this.#rapier,
      this.#world,
      tile,
      this.#config,
      this.#bridgeCollisionsByTile.get(tile.id) ?? [],
      (collider, surface) => this.#registerSurface(collider, surface),
      (collider) => this.#surfaces.delete(collider.handle),
    );
  }

  #removeTileColliders(tile: AttachedTileColliders): void {
    for (const collider of tile.colliders) {
      this.#surfaces.delete(collider.handle);
      this.#world.removeCollider(collider, false);
    }
  }

  #registerSurface(
    collider: RapierCollider,
    surface: PhysicsSurfaceMetadata,
  ): void {
    this.#surfaces.set(collider.handle, Object.freeze({ ...surface }));
  }

  #surfaceFor(collider: RapierCollider): PhysicsSurfaceMetadata {
    const surface = this.#surfaces.get(collider.handle);
    return surface === undefined
      ? {
          colliderId: "unknown",
          surfaceId: "unknown",
          classification: "unknown",
        }
      : { ...surface };
  }

  #getCharacter(playerId: string): AttachedCharacter {
    const character = this.#characters.get(playerId);
    if (character === undefined) {
      throw new Error(`Physics character ${playerId} is not attached`);
    }
    return character;
  }

  #getVehicle(id: string): AttachedVehicle {
    const vehicle = this.#vehicles.get(id);
    if (vehicle === undefined) throw new Error(`Physics vehicle ${id} is not attached`);
    return vehicle;
  }

  #vehicleGroundPose(
    position: Readonly<PhysicsVector3>,
    yawRadians: number,
    vehicle: AttachedVehicle,
  ): Readonly<{
    elevationM: number;
    pitchRadians: number;
    rollRadians: number;
    surface: PhysicsSurfaceMetadata;
  }> | null {
    const halfX = vehicle.groundContactHalfExtents.x;
    const halfZ = vehicle.groundContactHalfExtents.z;
    const samples: Readonly<{
      x: number;
      z: number;
      elevationM: number;
      surface: PhysicsSurfaceMetadata;
    }>[] = [];
    const cosine = Math.cos(yawRadians);
    const sine = Math.sin(yawRadians);
    for (const [x, z] of [[-halfX, -halfZ], [halfX, -halfZ], [-halfX, halfZ], [halfX, halfZ]] as const) {
      const origin = {
        x: position.x + x * cosine + z * sine,
        y: position.y + 2.4,
        z: position.z - x * sine + z * cosine,
      };
      const hit = this.#world.castRay(new this.#rapier.Ray(origin, { x: 0, y: -1, z: 0 }), 4.8, true, undefined, undefined, vehicle.collider);
      if (hit !== null) {
        samples.push({
          x,
          z,
          elevationM: origin.y - hit.timeOfImpact,
          surface: this.#surfaceFor(hit.collider),
        });
      }
    }
    if (samples.length < 3) return null;
    const average = (entries: readonly Readonly<{ elevationM: number }>[]) =>
      entries.reduce((total, sample) => total + sample.elevationM, 0) /
      entries.length;
    const front = samples.filter((sample) => sample.z > 0);
    const rear = samples.filter((sample) => sample.z < 0);
    const left = samples.filter((sample) => sample.x < 0);
    const right = samples.filter((sample) => sample.x > 0);
    if (front.length === 0 || rear.length === 0 || left.length === 0 || right.length === 0) {
      return null;
    }
    return {
      elevationM: average(samples),
      // Three.js and Rapier both use a right-handed frame here. Positive X
      // rotation lowers the +Z nose, hence the sign inversion.
      pitchRadians: -Math.atan2(average(front) - average(rear), halfZ * 2),
      rollRadians: Math.atan2(average(right) - average(left), halfX * 2),
      surface: dominantVehicleGroundSurface(samples),
    };
  }

  #assertMatchingCapsule(
    character: AttachedCharacter,
    capsule: Readonly<CharacterCapsuleSpec>,
  ): void {
    assertPositive(capsule.radiusM, "Character capsule radius");
    assertPositive(capsule.halfHeightM, "Character capsule half-height");
    if (
      Math.abs(character.capsule.radiusM - capsule.radiusM) >
        CAPSULE_MATCH_EPSILON ||
      Math.abs(character.capsule.halfHeightM - capsule.halfHeightM) >
        CAPSULE_MATCH_EPSILON
    ) {
      throw new Error(
        "Character motion capsule must match the attached character capsule",
      );
    }
  }

  #readCharacterContacts(
    controller: RapierCharacterController,
  ): readonly CharacterContact[] {
    const contacts: CharacterContact[] = [];
    for (
      let index = 0;
      index < controller.numComputedCollisions();
      index += 1
    ) {
      const collision = controller.computedCollision(index);
      if (collision?.collider === null || collision === null) {
        continue;
      }
      const surface = this.#surfaceFor(collision.collider);
      contacts.push({
        obstructionId: surface.colliderId,
        surface,
        normal: copyVector(collision.normal1),
        witnessPoint: copyVector(collision.witness1),
        timeOfImpact: collision.toi,
      });
    }
    return contacts;
  }

  #probeCharacterGround(
    character: AttachedCharacter,
    maxDistanceM: number,
  ): GroundProbeResult {
    const position = character.collider.translation();
    const centerOffsetM = characterCenterOffsetM(
      character.capsule,
      this.#config,
    );
    const ray = new this.#rapier.Ray(position, { x: 0, y: -1, z: 0 });
    const hit = this.#world.castRayAndGetNormal(
      ray,
      centerOffsetM + maxDistanceM + this.#config.characterContactOffsetM,
      true,
      undefined,
      undefined,
      character.collider,
    );
    if (hit === null) {
      return unavailableGround();
    }

    const distanceToGroundM = Math.max(
      0,
      hit.timeOfImpact - centerOffsetM,
    );
    const normal = normalizeVector(hit.normal);
    const slopeAngleRadians = Math.acos(clamp(normal.y, -1, 1));
    return {
      grounded: distanceToGroundM <=
          maxDistanceM + this.#config.characterContactOffsetM &&
        slopeAngleRadians <= character.maxSlopeAngleRadians,
      distanceToGroundM,
      normal,
      slopeAngleRadians,
      surface: this.#surfaceFor(hit.collider),
    };
  }

  #queryCharacterMantle(
    character: AttachedCharacter,
    forward: Readonly<PhysicsPlanarVector>,
    maxHeightM: number,
  ): MantleQueryResult {
    assertFinite(forward.x, "Mantle forward x");
    assertFinite(forward.z, "Mantle forward z");
    if (maxHeightM <= 0) {
      return unavailableMantle();
    }
    const planarLength = Math.hypot(forward.x, forward.z);
    if (planarLength <= MIN_VECTOR_LENGTH) {
      return unavailableMantle();
    }
    const direction = {
      x: forward.x / planarLength,
      y: 0,
      z: forward.z / planarLength,
    };
    const position = copyVector(character.collider.translation());
    const centerOffsetM = characterCenterOffsetM(
      character.capsule,
      this.#config,
    );
    const footElevationM = position.y - centerOffsetM;
    const obstacleProbeHeightM = Math.min(
      maxHeightM,
      character.capsule.radiusM,
    );
    const obstacleRay = new this.#rapier.Ray(
      {
        x: position.x,
        y: footElevationM + obstacleProbeHeightM,
        z: position.z,
      },
      direction,
    );
    const obstacle = this.#world.castRayAndGetNormal(
      obstacleRay,
      this.#config.mantleForwardDistanceM,
      true,
      undefined,
      undefined,
      character.collider,
    );
    if (obstacle === null) {
      return unavailableMantle();
    }

    const landingAdvanceM =
      obstacle.timeOfImpact +
      character.capsule.radiusM +
      this.#config.mantleClearanceM;
    const landingX = position.x + direction.x * landingAdvanceM;
    const landingZ = position.z + direction.z * landingAdvanceM;
    const topRayOriginY =
      footElevationM + maxHeightM + this.#config.mantleClearanceM;
    const landingProbe = this.#world.castRayAndGetNormal(
      new this.#rapier.Ray(
        { x: landingX, y: topRayOriginY, z: landingZ },
        { x: 0, y: -1, z: 0 },
      ),
      maxHeightM + this.#config.mantleClearanceM,
      true,
      undefined,
      undefined,
      character.collider,
    );
    if (landingProbe === null) {
      return unavailableMantle();
    }

    const landingElevationM =
      topRayOriginY - landingProbe.timeOfImpact;
    const heightM = landingElevationM - footElevationM;
    if (
      heightM <= character.maxStepHeightM + STEP_DETECTION_EPSILON ||
      heightM > maxHeightM + STEP_DETECTION_EPSILON ||
      landingProbe.normal.y <= 0
    ) {
      return unavailableMantle();
    }

    const targetPosition = {
      x: landingX,
      y: landingElevationM,
      z: landingZ,
    };
    const overlap = this.#world.intersectionWithShape(
      characterCenterFromFootPosition(
        targetPosition,
        character.capsule,
        this.#config,
      ),
      IDENTITY_ROTATION,
      new this.#rapier.Capsule(
        character.capsule.halfHeightM,
        character.capsule.radiusM,
      ),
      undefined,
      undefined,
      character.collider,
      undefined,
      (candidate) => candidate.handle !== landingProbe.collider.handle,
    );
    if (overlap !== null) {
      return unavailableMantle();
    }

    const obstacleSurface = this.#surfaceFor(obstacle.collider);
    return {
      possible: true,
      targetPosition,
      heightM,
      obstructionId: obstacleSurface.colliderId,
      landingSurface: this.#surfaceFor(landingProbe.collider),
    };
  }

  #assertUsable(): void {
    if (this.#disposed) {
      throw new Error("Physics world is disposed");
    }
  }

  #synchronizeSceneQueries(): void {
    if (!this.#sceneQueriesDirty) {
      return;
    }
    // Rapier 0.19 updates its broad phase during World.step(). This world has
    // zero gravity and no dynamic bodies, so this is a query-index refresh,
    // not hidden simulation integration.
    this.#world.step();
    this.#sceneQueriesDirty = false;
  }
}

function unavailableGround(): GroundProbeResult {
  return {
    grounded: false,
    distanceToGroundM: null,
    normal: null,
    slopeAngleRadians: null,
    surface: null,
  };
}

function unavailableMantle(): MantleQueryResult {
  return {
    possible: false,
    targetPosition: null,
    heightM: null,
    obstructionId: null,
    landingSurface: null,
  };
}

function copyVector(vector: Readonly<PhysicsVector3>): PhysicsVector3 {
  return { x: vector.x, y: vector.y, z: vector.z };
}

function characterCenterOffsetM(
  capsule: Readonly<CharacterCapsuleSpec>,
  config: Readonly<PhysicsConfig>,
): number {
  return (
    capsule.halfHeightM +
    capsule.radiusM +
    config.characterContactOffsetM
  );
}

function characterCenterFromFootPosition(
  footPosition: Readonly<PhysicsVector3>,
  capsule: Readonly<CharacterCapsuleSpec>,
  config: Readonly<PhysicsConfig>,
): PhysicsVector3 {
  return {
    x: footPosition.x,
    y: footPosition.y + characterCenterOffsetM(capsule, config),
    z: footPosition.z,
  };
}

function addVectors(
  left: Readonly<PhysicsVector3>,
  right: Readonly<PhysicsVector3>,
): PhysicsVector3 {
  return {
    x: left.x + right.x,
    y: left.y + right.y,
    z: left.z + right.z,
  };
}

function subtractVectors(
  left: Readonly<PhysicsVector3>,
  right: Readonly<PhysicsVector3>,
): PhysicsVector3 {
  return {
    x: left.x - right.x,
    y: left.y - right.y,
    z: left.z - right.z,
  };
}

function scaleVector(
  vector: Readonly<PhysicsVector3>,
  scale: number,
): PhysicsVector3 {
  return {
    x: vector.x * scale,
    y: vector.y * scale,
    z: vector.z * scale,
  };
}

function dominantVehicleGroundSurface(
  samples: readonly Readonly<{ surface: PhysicsSurfaceMetadata }>[],
): PhysicsSurfaceMetadata {
  const counts = new Map<string, { count: number; surface: PhysicsSurfaceMetadata }>();
  for (const sample of samples) {
    const current = counts.get(sample.surface.surfaceId);
    counts.set(sample.surface.surfaceId, {
      count: (current?.count ?? 0) + 1,
      surface: sample.surface,
    });
  }
  const dominant = [...counts.values()].sort((left, right) =>
    right.count - left.count ||
    left.surface.surfaceId.localeCompare(right.surface.surfaceId)
  )[0];
  if (dominant === undefined) {
    throw new Error("Vehicle ground pose requires at least one surface sample");
  }
  return dominant.surface;
}

function vectorLength(vector: Readonly<PhysicsVector3>): number {
  return Math.hypot(vector.x, vector.y, vector.z);
}

function normalizeVector(vector: Readonly<PhysicsVector3>): PhysicsVector3 {
  const length = vectorLength(vector);
  return length <= MIN_VECTOR_LENGTH
    ? { x: 0, y: 0, z: 0 }
    : scaleVector(vector, 1 / length);
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function vehicleRotation(
  yawRadians: number,
  pitchRadians: number,
  rollRadians: number,
): Readonly<{ x: number; y: number; z: number; w: number }> {
  const yaw = {
    x: 0,
    y: Math.sin(yawRadians / 2),
    z: 0,
    w: Math.cos(yawRadians / 2),
  };
  const pitch = {
    x: Math.sin(pitchRadians / 2),
    y: 0,
    z: 0,
    w: Math.cos(pitchRadians / 2),
  };
  const roll = {
    x: 0,
    y: 0,
    z: Math.sin(rollRadians / 2),
    w: Math.cos(rollRadians / 2),
  };
  return multiplyQuaternions(multiplyQuaternions(yaw, pitch), roll);
}

function multiplyQuaternions(
  left: Readonly<{ x: number; y: number; z: number; w: number }>,
  right: Readonly<{ x: number; y: number; z: number; w: number }>,
): Readonly<{ x: number; y: number; z: number; w: number }> {
  return {
    x: left.w * right.x + left.x * right.w + left.y * right.z - left.z * right.y,
    y: left.w * right.y - left.x * right.z + left.y * right.w + left.z * right.x,
    z: left.w * right.z + left.x * right.y - left.y * right.x + left.z * right.w,
    w: left.w * right.w - left.x * right.x - left.y * right.y - left.z * right.z,
  };
}

function assertStableId(id: string, label: string): void {
  if (id.trim().length === 0) {
    throw new Error(`${label} must be non-empty`);
  }
}

function assertTick(tick: number): void {
  if (!Number.isSafeInteger(tick) || tick < 0) {
    throw new Error("Character motion tick must be a non-negative integer");
  }
}

function assertSlopeAngle(angle: number): void {
  if (!Number.isFinite(angle) || angle < 0 || angle >= Math.PI / 2) {
    throw new Error(
      "Character maximum slope angle must be within [0, PI / 2)",
    );
  }
}

function assertPositive(value: number, label: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${label} must be positive and finite`);
  }
}

function assertFiniteNonNegative(value: number, label: string): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`${label} must be non-negative and finite`);
  }
}

function assertFinite(value: number, label: string): void {
  if (!Number.isFinite(value)) {
    throw new Error(`${label} must be finite`);
  }
}

function assertVector(
  vector: Readonly<PhysicsVector3>,
  label: string,
): void {
  assertFinite(vector.x, `${label} x`);
  assertFinite(vector.y, `${label} y`);
  assertFinite(vector.z, `${label} z`);
}
